import { Helmet } from 'react-helmet-async';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { saveAs } from 'file-saver';
import { FaEye, FaPaperclip, FaTimes } from 'react-icons/fa';
import TableData from '../components/common/TableData';
import Loader from '../components/common/Loader';
import {
  assignSupportTicket,
  downloadSupportAttachment,
  getSupportAssignees,
  getSupportSummary,
  getSupportTicket,
  getSupportTickets,
  replySupportTicket,
  setSupportTicketStatus,
} from '../Servies/SupportTicketServices';

// Same limits the backend enforces; checked here so the user sees the message before uploading.
const MAX_FILES = 2;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'heic', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'txt'];

const headers = ['Ticket No', 'User', 'Phone', 'Category', 'Last Message', 'Last Activity', 'Assigned To', 'Status', 'Action'];

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '-');

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// The api instance never rejects on HTTP status, so every call is checked here.
const unwrap = (res) => {
  if (res?.data?.success) return res.data.data;
  throw new Error(res?.data?.message || 'Something went wrong!');
};

const StatusChip = ({ status }) => (
  <Chip size="small" label={status} color={status === 'OPEN' ? 'success' : 'default'} variant={status === 'OPEN' ? 'filled' : 'outlined'} />
);

export default function SupportTickets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState({ open: 0, closed: 0, awaitingSupport: 0, assignedToMe: 0 });
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState('ALL');
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [assignedTo, setAssignedTo] = useState('ANY');
  const [assignees, setAssignees] = useState([]);
  const [assigneeId, setAssigneeId] = useState('');
  const [assignNote, setAssignNote] = useState('');
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [ticket, setTicket] = useState(null);
  const [reply, setReply] = useState('');
  const [files, setFiles] = useState([]);
  const [closeAfterReply, setCloseAfterReply] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef(null);
  const threadEndRef = useRef(null);

  const fetchSummary = async () => {
    try {
      setSummary(unwrap(await getSupportSummary()));
    } catch (error) {
      console.error(error);
    }
  };

  // filters overrides the current filter state, for callers that set filters and fetch in the same click.
  const fetchTickets = async (page = pageNo, filters = {}) => {
    const f = { status, unreadOnly, assignedTo, ...filters };
    setIsLoading(true);
    try {
      const data = unwrap(
        await getSupportTickets({
          status: f.status === 'ALL' ? undefined : f.status,
          category: category || undefined,
          q: search || undefined,
          unreadOnly: f.unreadOnly,
          assignedTo: f.assignedTo === 'ANY' ? undefined : f.assignedTo,
          page: page - 1,
          size: pageSize,
        })
      );
      setTickets(data.items || []);
      setTotalRecords(data.totalItems || 0);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const refresh = () => {
    fetchSummary();
    fetchTickets();
  };

  // ADMIN and SUPPORT users for the "Assigned to" filter and the "Reassign to" dropdown.
  const fetchAssignees = () =>
    getSupportAssignees()
      .then((res) => setAssignees(unwrap(res) || []))
      .catch((error) => console.error(error));

  useEffect(() => {
    fetchSummary();
    fetchAssignees();
  }, []);

  // The assignment email links to /dashboard/SupportTickets?ticketId=123: open that ticket straight away.
  useEffect(() => {
    const linked = searchParams.get('ticketId');
    if (linked && /^\d+$/.test(linked)) {
      openTicket(linked);
      searchParams.delete('ticketId');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [pageNo, pageSize]);

  useEffect(() => {
    if (ticket) threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages?.length]);

  const handleSearch = (filters) => {
    if (pageNo === 1) fetchTickets(1, filters);
    else setPageNo(1);
  };

  const applyQuickFilter = (filters) => {
    if (filters.status !== undefined) setStatus(filters.status);
    if (filters.unreadOnly !== undefined) setUnreadOnly(filters.unreadOnly);
    if (filters.assignedTo !== undefined) setAssignedTo(filters.assignedTo);
    handleSearch(filters);
  };

  const openTicket = async (ticketId) => {
    setIsLoading(true);
    try {
      setTicket(unwrap(await getSupportTicket(ticketId)));
      setReply('');
      setFiles([]);
      setCloseAfterReply(false);
      setAssigneeId('');
      setAssignNote('');
      fetchAssignees();
      // Opening clears adminUnread and, the first time, assigns the ticket to you; refresh counts and rows.
      refresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const closeDialog = () => {
    if (isSending) return;
    setTicket(null);
  };

  const handleFilesPicked = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';
    const next = [...files, ...picked];
    if (next.length > MAX_FILES) {
      toast.error(`You can attach up to ${MAX_FILES} files`);
      return;
    }
    const tooBig = picked.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) {
      toast.error('Each file must be 5 MB or smaller');
      return;
    }
    const badType = picked.find((f) => !ALLOWED_EXTENSIONS.includes((f.name.split('.').pop() || '').toLowerCase()));
    if (badType) {
      toast.error(`Unsupported file type: ${badType.name}`);
      return;
    }
    setFiles(next);
  };

  const sendReply = async () => {
    if (!reply.trim() && files.length === 0) {
      toast.warn('Write a reply or attach a file');
      return;
    }
    setIsSending(true);
    try {
      const updated = unwrap(await replySupportTicket(ticket.ticketId, reply.trim(), files, closeAfterReply));
      setTicket(updated);
      setReply('');
      setFiles([]);
      setCloseAfterReply(false);
      toast.success('Reply sent');
      refresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSending(false);
    }
  };

  const toggleStatus = async () => {
    const target = ticket.status === 'OPEN' ? 'CLOSED' : 'OPEN';
    setIsSending(true);
    try {
      setTicket(unwrap(await setSupportTicketStatus(ticket.ticketId, target)));
      toast.success(target === 'CLOSED' ? 'Ticket closed' : 'Ticket reopened');
      refresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSending(false);
    }
  };

  const reassign = async () => {
    if (!assigneeId) {
      toast.warn('Choose a user to assign the ticket to');
      return;
    }
    setIsSending(true);
    try {
      const res = await assignSupportTicket(ticket.ticketId, assigneeId, assignNote.trim() || undefined);
      const updated = unwrap(res);
      setTicket(updated);
      setAssigneeId('');
      setAssignNote('');
      // The message says whether the email went out; warn when it didn't.
      if (updated.emailSent === false) toast.warn(res.data.message);
      else toast.success(res.data.message);
      refresh();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSending(false);
    }
  };

  const downloadAttachment = async (attachment) => {
    try {
      const res = await downloadSupportAttachment(attachment.downloadUrl);
      if (res?.status !== 200) {
        // Errors come back as a JSON blob because of responseType: 'blob'.
        let message = 'Could not download the file';
        try {
          message = JSON.parse(await res.data.text()).message || message;
        } catch (e) {
          // keep the default message
        }
        throw new Error(message);
      }
      saveAs(res.data, attachment.fileName);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const rows = tickets.map((t) => ({
    'Ticket No': (
      <Stack direction="row" spacing={1} alignItems="center">
        <span>{t.ticketNo}</span>
        {t.adminUnread && <Chip size="small" color="error" label="New" />}
      </Stack>
    ),
    User: t.user?.name || t.user?.userId || '-',
    Phone: t.user?.phone || '-',
    Category: t.category || '-',
    'Last Message': (
      <Box sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.lastMessagePreview || ''}>
        {t.lastMessageBy === 'ADMIN' ? 'Support: ' : ''}
        {t.lastMessagePreview || '-'}
      </Box>
    ),
    'Last Activity': formatDate(t.lastMessageAt || t.updatedAt),
    'Assigned To': t.assignedTo ? (
      t.assignedTo.name || t.assignedTo.userId
    ) : (
      <Typography variant="body2" color="text.secondary">
        Unassigned
      </Typography>
    ),
    Status: <StatusChip status={t.status} />,
    Action: (
      <IconButton color="primary" onClick={() => openTicket(t.ticketId)} title="Open ticket">
        <FaEye />
      </IconButton>
    ),
  }));

  const summaryCards = [
    { label: 'My open tickets', value: summary.assignedToMe, onClick: () => applyQuickFilter({ status: 'OPEN', unreadOnly: false, assignedTo: 'me' }) },
    { label: 'Open', value: summary.open, onClick: () => applyQuickFilter({ status: 'OPEN', unreadOnly: false, assignedTo: 'ANY' }) },
    { label: 'Awaiting support', value: summary.awaitingSupport, onClick: () => applyQuickFilter({ status: 'ALL', unreadOnly: true, assignedTo: 'ANY' }) },
    { label: 'Closed', value: summary.closed, onClick: () => applyQuickFilter({ status: 'CLOSED', unreadOnly: false, assignedTo: 'ANY' }) },
  ];

  const assignment = ticket?.assignment;
  const currentAssigneeId = assignment?.assignedTo?.userId;
  const reassignOptions = assignees.filter((a) => a.userId !== currentAssigneeId);

  return (
    <>
      <ToastContainer />
      <Helmet>
        <title> Support Tickets </title>
      </Helmet>
      {isLoading && <Loader />}

      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom>
          Support Tickets
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {summaryCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.label}>
              <Paper variant="outlined" sx={{ p: 2, cursor: 'pointer' }} onClick={card.onClick}>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography variant="h4">{card.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} sx={{ mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="support-status">Status</InputLabel>
            <Select labelId="support-status" label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="OPEN">Open</MenuItem>
              <MenuItem value="CLOSED">Closed</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="support-assigned">Assigned to</InputLabel>
            <Select labelId="support-assigned" label="Assigned to" value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)}>
              <MenuItem value="ANY">Anyone</MenuItem>
              <MenuItem value="me">Me</MenuItem>
              <MenuItem value="none">Unassigned</MenuItem>
              {assignees.map((a) => (
                <MenuItem key={a.userId} value={a.userId}>
                  {a.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" label="Category" value={category} onChange={(e) => setCategory(e.target.value)} />
          <TextField
            size="small"
            label="Ticket no / phone / user id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch({})}
            sx={{ minWidth: 260 }}
          />
          <FormControlLabel
            control={<Checkbox checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />}
            label="Awaiting support only"
          />
          <Button variant="contained" onClick={() => handleSearch({})}>
            Search
          </Button>
        </Stack>

        <TableData
          data={rows}
          headers={headers}
          rowsPerPageOptions={[10, 20, 50]}
          page={pageNo}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={setPageNo}
          onPageSizeChange={setPageSize}
        />
      </Container>

      <Dialog open={!!ticket} onClose={closeDialog} fullWidth maxWidth="md">
        {ticket && (
          <>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{ticket.ticketNo}</span>
                  <StatusChip status={ticket.status} />
                  {ticket.category && <Chip size="small" variant="outlined" label={ticket.category} />}
                  {ticket.source === 'LEGACY_FEEDBACK' && <Chip size="small" variant="outlined" label="From feedback" />}
                </Stack>
                <IconButton onClick={closeDialog} disabled={isSending}>
                  <FaTimes />
                </IconButton>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {[ticket.user?.name, ticket.user?.phone, ticket.user?.email]
                  .filter((v) => v && v !== 'NULL')
                  .join(' · ') || ticket.user?.userId}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Raised {formatDate(ticket.createdAt)}
                {ticket.closedAt ? ` · Closed ${formatDate(ticket.closedAt)}` : ''}
              </Typography>
            </DialogTitle>

            <Box sx={{ px: 3, pb: 2 }}>
              <Typography variant="body2">
                Assigned to <b>{assignment?.assignedTo?.name || assignment?.assignedTo?.userId || 'nobody'}</b>
                {assignment?.assignedAt && (
                  <Typography component="span" variant="caption" color="text.secondary">
                    {' '}
                    · by {assignment.assignedBy?.name || assignment.assignedBy?.userId} on {formatDate(assignment.assignedAt)}
                  </Typography>
                )}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }} alignItems={{ sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel id="support-reassign">Reassign to</InputLabel>
                  <Select
                    labelId="support-reassign"
                    label="Reassign to"
                    value={assigneeId}
                    onChange={(e) => setAssigneeId(e.target.value)}
                    disabled={isSending}
                  >
                    {reassignOptions.length === 0 && (
                      <MenuItem value="" disabled>
                        No other ADMIN or SUPPORT users
                      </MenuItem>
                    )}
                    {reassignOptions.map((a) => (
                      <MenuItem key={a.userId} value={a.userId}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                          <span>{a.name}</span>
                          {(a.roles || []).map((r) => (
                            <Chip key={r} size="small" variant="outlined" label={r} />
                          ))}
                          {!a.email && (
                            <Typography variant="caption" color="text.secondary">
                              (no email)
                            </Typography>
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Note for the assignee (optional)"
                  value={assignNote}
                  onChange={(e) => setAssignNote(e.target.value)}
                  inputProps={{ maxLength: 1000 }}
                  disabled={isSending}
                  sx={{ flex: 1 }}
                />
                <Button variant="outlined" onClick={reassign} disabled={isSending || !assigneeId}>
                  Assign
                </Button>
              </Stack>
              {(assignment?.history || []).length > 1 && (
                <Box sx={{ mt: 1 }}>
                  {assignment.history.map((h, i) => (
                    <Typography key={i} variant="caption" color="text.secondary" component="div">
                      {formatDate(h.at)} ·{' '}
                      {h.type === 'AUTO'
                        ? `Assigned to ${h.to?.name || h.to?.userId} on first open`
                        : `${h.by?.name || h.by?.userId} assigned to ${h.to?.name || h.to?.userId}`}
                      {h.note ? ` · "${h.note}"` : ''}
                      {h.emailSent === true ? ' · email sent' : ''}
                      {h.emailSent === false ? ' · email not sent' : ''}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>

            <DialogContent dividers sx={{ bgcolor: 'background.neutral', maxHeight: '50vh' }}>
              <Stack spacing={2}>
                {(ticket.messages || []).map((m) => {
                  const fromSupport = m.sender === 'ADMIN';
                  return (
                    <Box key={m.messageId} sx={{ display: 'flex', justifyContent: fromSupport ? 'flex-end' : 'flex-start' }}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          maxWidth: '75%',
                          bgcolor: fromSupport ? 'primary.lighter' : 'background.paper',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {fromSupport ? 'Support' : 'User'} · {formatDate(m.createdAt)}
                        </Typography>
                        {m.message && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {m.message}
                          </Typography>
                        )}
                        {(m.attachments || []).length > 0 && (
                          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                            {m.attachments.map((a) => (
                              <Chip
                                key={a.attachmentId}
                                size="small"
                                icon={<FaPaperclip />}
                                label={`${a.fileName} (${formatSize(a.sizeBytes)})`}
                                onClick={() => downloadAttachment(a)}
                                sx={{ ml: '0 !important' }}
                              />
                            ))}
                          </Stack>
                        )}
                      </Paper>
                    </Box>
                  );
                })}
                <div ref={threadEndRef} />
              </Stack>
            </DialogContent>

            <DialogActions sx={{ display: 'block', p: 2 }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={8}
                placeholder={ticket.status === 'CLOSED' ? 'Replying will reopen this ticket' : 'Write a reply'}
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                inputProps={{ maxLength: 5000 }}
                disabled={isSending}
              />
              {files.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  {files.map((f, i) => (
                    <Chip
                      key={`${f.name}-${i}`}
                      size="small"
                      label={`${f.name} (${formatSize(f.size)})`}
                      onDelete={() => setFiles(files.filter((_, j) => j !== i))}
                      sx={{ ml: '0 !important' }}
                    />
                  ))}
                </Stack>
              )}
              <Divider sx={{ my: 1.5 }} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    accept={ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',')}
                    onChange={handleFilesPicked}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<FaPaperclip />}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSending || files.length >= MAX_FILES}
                  >
                    Attach ({files.length}/{MAX_FILES})
                  </Button>
                  <FormControlLabel
                    control={<Checkbox checked={closeAfterReply} onChange={(e) => setCloseAfterReply(e.target.checked)} disabled={isSending} />}
                    label="Close ticket after reply"
                  />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" color={ticket.status === 'OPEN' ? 'error' : 'success'} onClick={toggleStatus} disabled={isSending}>
                    {ticket.status === 'OPEN' ? 'Close ticket' : 'Reopen ticket'}
                  </Button>
                  <Button variant="contained" onClick={sendReply} disabled={isSending}>
                    {isSending ? 'Sending...' : 'Send reply'}
                  </Button>
                </Stack>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
